import { DEFAULT_LOCALE, type SupportedLocale } from "./config";
import { pickAppCopy } from "./pickAppCopy";

/** Canonical side values stored in the database. */
export const SIDE_VALUES = ["Left", "Right", "Both", "Middle", "N/A"] as const;
export type SideValue = (typeof SIDE_VALUES)[number];

/** Side options shown in procedure and injection selects. */
export const PROCEDURE_SIDE_OPTIONS = ["Both", "Left", "Right", "Middle"] as const;
export type ProcedureSideValue = (typeof PROCEDURE_SIDE_OPTIONS)[number];

const SIDE_LABELS: Record<SupportedLocale, Record<SideValue, string>> = {
    "en-GB": {
        Left: "Left",
        Right: "Right",
        Both: "Both",
        Middle: "Middle",
        "N/A": "Not applicable",
    },
    "en-US": {
        Left: "Left",
        Right: "Right",
        Both: "Both",
        Middle: "Middle",
        "N/A": "Not applicable",
    },
    "de-DE": {
        Left: "Links",
        Right: "Rechts",
        Both: "Beidseitig",
        Middle: "Mittig",
        "N/A": "Nicht zutreffend",
    },
    "da-DK": {
        Left: "Venstre",
        Right: "Højre",
        Both: "Begge",
        Middle: "Midt",
        "N/A": "Ikke relevant",
    },
    "fr-FR": {
        Left: "Gauche",
        Right: "Droit",
        Both: "Les deux",
        Middle: "Milieu",
        "N/A": "Sans objet",
    },
    "it-IT": {
        Left: "Sinistra",
        Right: "Destra",
        Both: "Entrambi",
        Middle: "Centro",
        "N/A": "Non applicabile",
    },
    "es-ES": {
        Left: "Izquierda",
        Right: "Derecha",
        Both: "Ambos",
        Middle: "Centro",
        "N/A": "No aplicable",
    },
};

export function isSideValue(value: string | null | undefined): value is SideValue {
    return value != null && (SIDE_VALUES as readonly string[]).includes(value);
}

/** Localised label for a stored side enum value. Returns the raw value when unknown. */
export function formatSide(side: string | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
    if (!side) return "";
    if (isSideValue(side)) return pickAppCopy(SIDE_LABELS, locale)[side];
    return side;
}

export interface SideSelectOption {
    label: string;
    value: ProcedureSideValue;
}

/** Select options for procedure side fields. Values stay canonical; labels are localised. */
export function getSideSelectOptions(locale: SupportedLocale = DEFAULT_LOCALE): SideSelectOption[] {
    return PROCEDURE_SIDE_OPTIONS.map((value) => ({
        value,
        label: pickAppCopy(SIDE_LABELS, locale)[value],
    }));
}
